import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { useEffect } from 'react';
import smartlookClient from 'smartlook-client';

import { blockList } from '@/configs/blocklist';
import useUserStore, { type UserT } from '@/stores/UserStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

const useTrackInit = () => {
  const user = useUserStore((state) => state.user);
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  useEffect(() => {
    if (!user) return;
    if (!currWorkspace) return;
    if (import.meta.env.VITE_PUBLIC_ENVIRONMENT !== 'prod') return;
    if (blockList.includes(user?.email)) return;
    if (user?.email.includes('@deepbluedot.io')) return;

    initAmplitude(user, currWorkspace);
    initSmartlook(user, currWorkspace);
    initGtm(user, currWorkspace);
  }, [user, currWorkspace]);
};

const initAmplitude = async (user: UserT, workspace: WorkspaceT) => {
  const amplitude = await import('@amplitude/analytics-browser');
  // amplitude
  const sessionReplayTracking = sessionReplayPlugin();
  const guidesAndSurveysTracking = engagementPlugin();
  amplitude.add(sessionReplayTracking as any);
  amplitude.add(guidesAndSurveysTracking as any);
  amplitude.init(
    import.meta.env.VITE_PUBLIC_AMPLITUDE_KEY as string,
    user.email,
    {
      autocapture: {
        elementInteractions: true,
        pageViews: false,
      },
      defaultTracking: {
        pageViews: false,
      },
    },
  );
  const identifyObj = new amplitude.Identify();
  identifyObj.set('company', user.email.split('@')[1].split('.')[0]);
  identifyObj.set('workspace_id', workspace.alias);
  identifyObj.set('workspace_name', workspace.name);
  amplitude.identify(identifyObj);
};

const initSmartlook = (user: UserT, workspace: WorkspaceT) => {
  // smartlook
  smartlookClient.init(
    import.meta.env.VITE_PUBLIC_SMARTLOOK_API_KEY as string,
    {
      region: 'us',
    },
  );
  smartlookClient.identify(user.email, {
    first_name: user.firstName,
    email: user.email,
    workspace: workspace.name,
  });
};

const initGtm = (user: UserT, workspace: WorkspaceT) => {
  // gtm은 사용자 관계없이 수집
  if (!window.dataLayer) return;
  window.dataLayer.push({
    event: 'user_context',
    user: {
      id: user.user_id,
      email: user.email,
    },
    workspace: {
      id: workspace.alias,
      name: workspace.name,
    },
  });
};

export default useTrackInit;
